import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import LeadCampaignApi from "../../../../../infrastructure/api/leadCampaignApi";
import AdminActionConfirmDialog from "../components/AdminActionConfirmDialog";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import { buildMarketGrowthPath } from "../marketGrowthMenuRouteConfig";
import ImportContactsDialog from "./components/ImportContactsDialog";
import LeadContactFormDialog from "./components/LeadContactFormDialog";
import LeadContactGroupFormDialog from "./components/LeadContactGroupFormDialog";

const LeadContactGroupDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const groupId = Number(id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [group, setGroup] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [deleteContactTarget, setDeleteContactTarget] = useState(null);
  const [contactDeleting, setContactDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const [groupRes, contactsRes] = await Promise.all([
        LeadCampaignApi.getGroupById(groupId),
        LeadCampaignApi.getGroupContacts(groupId, { page: 1, pageSize: 100 }),
      ]);
      const groupNorm = LeadCampaignApi.normalize(groupRes, "بارگذاری گروه ناموفق بود.");
      const contactsNorm = LeadCampaignApi.normalize(contactsRes, "بارگذاری مخاطبین ناموفق بود.");
      if (groupNorm.error) {
        setError(groupNorm.error);
        return;
      }
      setGroup(groupNorm.data);
      setContacts(contactsNorm.data?.items || contactsNorm.data?.Items || []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleImport = async (rawText) => {
    setImporting(true);
    try {
      const res = await LeadCampaignApi.importContacts(groupId, rawText);
      const normalized = LeadCampaignApi.normalize(res, "واردسازی ناموفق بود.");
      if (normalized.error) return { error: normalized.error };
      toast.success("واردسازی انجام شد.");
      await load();
      return { data: normalized.data };
    } catch (e) {
      return { error: e?.message || "خطا" };
    } finally {
      setImporting(false);
    }
  };

  const handleEditSubmit = async (payload) => {
    setSaving(true);
    try {
      const res = await LeadCampaignApi.updateGroup(groupId, payload);
      const normalized = LeadCampaignApi.normalize(res, "ویرایش گروه ناموفق بود.");
      if (normalized.error) return { error: normalized.error };
      toast.success("گروه ویرایش شد.");
      setEditOpen(false);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await LeadCampaignApi.deleteGroup(groupId);
      const normalized = LeadCampaignApi.normalize(res, "حذف گروه ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success("گروه حذف شد.");
      navigate(buildMarketGrowthPath("LeadCampaigns", "Groups"));
    } catch (e) {
      toast.error(e?.message || "خطا در حذف");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleContactEditSubmit = async (payload) => {
    if (!editingContact) return { error: "مخاطب انتخاب نشده است." };
    setContactSaving(true);
    try {
      const res = await LeadCampaignApi.updateContact(groupId, editingContact.id, payload);
      const normalized = LeadCampaignApi.normalize(res, "ویرایش شماره ناموفق بود.");
      if (normalized.error) return { error: normalized.error };
      toast.success("شماره موبایل ویرایش شد.");
      setEditingContact(null);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setContactSaving(false);
    }
  };

  const handleContactDelete = async () => {
    if (!deleteContactTarget) return;
    setContactDeleting(true);
    try {
      const res = await LeadCampaignApi.deleteContact(groupId, deleteContactTarget.id);
      const normalized = LeadCampaignApi.normalize(res, "حذف مخاطب ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success("مخاطب حذف شد.");
      setDeleteContactTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در حذف");
    } finally {
      setContactDeleting(false);
    }
  };

  const tableActions = useMemo(
    () => [
      {
        icon: <EditIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setEditingContact(row),
        color: "secondary",
        tooltip: "ویرایش شماره",
      },
      {
        icon: <DeleteIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDeleteContactTarget(row),
        color: "error",
        tooltip: "حذف مخاطب",
      },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      contacts.map((row) => ({
        ...row,
        validLabel: row.isValidMobile ? "بله" : "خیر",
        registeredLabel: row.isRegistered ? "بله" : "خیر",
        projectLabel: row.hasProjectRequest ? "بله" : "خیر",
      })),
    [contacts]
  );

  const headCells = useMemo(
    () => [
      { id: "mobile", label: "موبایل" },
      { id: "validLabel", label: "معتبر" },
      { id: "registeredLabel", label: "ثبت‌نام" },
      { id: "projectLabel", label: "درخواست پروژه" },
    ],
    []
  );

  return (
    <MarketGrowthPageShell
      title={group?.title || "جزئیات گروه مخاطب"}
      description={group?.description || "لیست مخاطبین و واردسازی شماره."}
      loading={loading}
      error={error}
      onRetry={load}
    >
      {group ? (
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            منبع: {group.sourceName || "—"} | وضعیت:{" "}
            <Chip size="small" label={group.isActive ? "فعال" : "غیرفعال"} />
            {group.tags ? ` | برچسب: ${group.tags}` : ""}
          </Typography>
        </Box>
      ) : null}
      <Box display="flex" flexWrap="wrap" gap={1} justifyContent="space-between" mb={2}>
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setEditOpen(true)}
            disabled={!group}
          >
            ویرایش گروه
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteOpen(true)}
            disabled={!group}
          >
            حذف گروه
          </Button>
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
            واردسازی شماره
          </Button>
        </Box>
        <IconButton onClick={load} aria-label="بارگذاری مجدد">
          <SyncIcon />
        </IconButton>
      </Box>
      <Card>
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            defaultSort={{ orderBy: "mobile", order: "asc" }}
          />
        </CardContent>
      </Card>
      <LeadContactGroupFormDialog
        open={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        onSubmit={handleEditSubmit}
        initialData={group}
        saving={saving}
      />
      <LeadContactFormDialog
        open={Boolean(editingContact)}
        onClose={() => !contactSaving && setEditingContact(null)}
        onSubmit={handleContactEditSubmit}
        initialData={editingContact}
        saving={contactSaving}
      />
      <ImportContactsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
        saving={importing}
      />
      <AdminActionConfirmDialog
        open={deleteOpen}
        title="حذف گروه مخاطب"
        description={
          group
            ? `آیا از حذف گروه «${group.title}» (${group.contactCount ?? contacts.length} مخاطب) مطمئن هستید؟ این عمل قابل بازگشت نیست.`
            : "آیا از حذف این گروه مطمئن هستید؟"
        }
        confirmLabel="حذف"
        confirmColor="error"
        showNotes={false}
        saving={deleting}
        onClose={() => !deleting && setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
      <AdminActionConfirmDialog
        open={Boolean(deleteContactTarget)}
        title="حذف مخاطب"
        description={
          deleteContactTarget
            ? `آیا از حذف شماره «${deleteContactTarget.mobile}» مطمئن هستید؟`
            : ""
        }
        confirmLabel="حذف"
        confirmColor="error"
        showNotes={false}
        saving={contactDeleting}
        onClose={() => !contactDeleting && setDeleteContactTarget(null)}
        onConfirm={handleContactDelete}
      />
    </MarketGrowthPageShell>
  );
};

export default LeadContactGroupDetailPage;
